import { useState } from "react";
import { toast } from "sonner";
import { ArrowSquareOut, Plugs } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { apiErrorMessage } from "@/lib/api";
import { Kpi, fmtGb, when } from "./Kpi";

function ConnectRailway({ reload }) {
  const [token, setToken] = useState("");
  const [kind, setKind] = useState("project");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/resources/railway-token", { token, kind }); setToken(""); toast.success("Railway connected — collecting metrics…"); setTimeout(reload, 8000); }
    catch (e) { toast.error(apiErrorMessage(e, "Could not connect Railway")); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-dashed border-slate-300 p-3 text-xs text-slate-600 space-y-2" data-testid="railway-connect">
      <div className="font-semibold text-slate-800">Not connected: no Railway metrics yet</div>
      <p>In Railway, open your WhatsApp project → <b>Settings → Tokens</b> → create a <b>Project token</b> for the production environment, then paste it here. It's saved encrypted on the server and never shown again.</p>
      <div className="flex gap-2 flex-wrap">
        <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 rounded border border-slate-200 bg-white px-2 text-xs" data-testid="railway-token-kind">
          <option value="project">Project token</option><option value="account">Account / workspace token</option>
        </select>
        <Input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Paste Railway token" className="h-8 flex-1 min-w-[200px]" data-testid="railway-token-input" />
        <Button size="sm" disabled={busy || token.length < 10} onClick={save} className="h-8 bg-[#327de3] text-white hover:bg-[#1f64c2]" data-testid="railway-token-save"><Plugs size={12} className="mr-1" /> Connect</Button>
      </div>
    </div>
  );
}

const S = (svc, k) => (svc.series?.[k] || []).map((x) => ({ ts: x.ts, v: x.v }));

export function RailwaySection({ d, limits, reload }) {
  const rw = d.railway || {};
  const disconnect = async () => { try { await api.delete("/admin/resources/railway-token"); toast.success("Railway token removed"); reload(); } catch (e) { toast.error(apiErrorMessage(e, "Failed")); } };
  const eu = rw.estimated_usage;
  return (
    <div className="min-w-0" data-testid="res-railway">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs font-bold text-slate-800">Railway <span className="font-normal text-slate-400">· {rw.project ? `project ${rw.project}` : "WhatsApp worker hosting"} · Hobby (manual, unverified)</span></div>
        <div className="flex items-center gap-2">
          {d.railway_token && <button onClick={disconnect} className="text-[11px] text-slate-400 hover:text-rose-500" data-testid="railway-disconnect">Remove token</button>}
          <a href="https://railway.com/dashboard" target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-[#327de3] inline-flex items-center gap-1" data-testid="res-railway-upgrade">Manage <ArrowSquareOut size={11} /></a>
        </div>
      </div>
      {!d.railway_token ? <ConnectRailway reload={reload} /> : (
        <div className="space-y-2">
          {(rw.errors || []).length > 0 && <div className="text-[11px] text-amber-600" data-testid="railway-errors">Railway API: {rw.errors.join(" · ")}</div>}
          {(rw.services || []).map((svc) => (
            <div key={svc.id} className="grid grid-cols-2 lg:grid-cols-3 gap-2" data-testid={`res-rw-svc-${svc.name}`}>
              <Kpi testId={`res-rw-cpu-${svc.name}`} title={`${svc.name} · CPU`} used={svc.current.CPU_USAGE} alloc={svc.current.CPU_LIMIT} fmt={(v) => `${(+v).toFixed(2)} vCPU`} series={S(svc, "CPU_USAGE")} limits={limits} source="Live · Railway API" color="#8b5cf6" note="Railway-reported vCPU in use vs. the service's vCPU limit, hourly over 7 days." />
              <Kpi testId={`res-rw-mem-${svc.name}`} title={`${svc.name} · Memory`} used={svc.current.MEMORY_USAGE_GB} alloc={svc.current.MEMORY_LIMIT_GB} fmt={fmtGb} series={S(svc, "MEMORY_USAGE_GB")} limits={limits} source="Live · Railway API" note="RAM used (Chromium + worker) vs. the service limit." />
              <Kpi testId={`res-rw-disk-${svc.name}`} title={`${svc.name} · Volume`} used={svc.current.DISK_USAGE_GB} alloc={null} fmt={fmtGb} series={S(svc, "DISK_USAGE_GB")} limits={limits} source="Live · Railway API" color="#10b981" note="Disk used by the /data volume (WhatsApp session). Media isn't stored here; it's uploaded to Emergent Object Storage." />
            </div>
          ))}
          {!rw.services && <div className="text-[11px] text-slate-500">Token saved. Click Refresh Now to collect metrics.</div>}
          <div className="text-[11px] text-slate-500" data-testid="railway-estimated">
            Estimated usage this billing cycle (Railway API, before plan credits):{" "}
            {eu ? Object.entries(eu).map(([k, v]) => `${k.replace(/_/g, " ").toLowerCase()} ${v}`).join(" · ") : "Not available"}
            {" "}· Hobby includes $5 of usage. Check actual charges on Railway's invoice page. Last collected {when(d.last_collected_at)}.
          </div>
        </div>
      )}
    </div>
  );
}
